import { Component, EventEmitter, Output, OnInit, OnDestroy, Input, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SearchService } from '../../services/search.service';
import { SearchResponse } from '../../models/search-response.interface';
import { debounceTime, switchMap, map, startWith, finalize, distinctUntilChanged } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ConfigService } from 'app/services/config.service';
import { SearchContext, SearchParams } from '../../models/search-request.interface';
import { SolutionFinderService } from 'app/services/solution-finder.service';
import { FilterComponent } from '../_common/filter/filter.component';

@Component({
  selector: 'app-devsite-search',
  templateUrl: './devsite-search.component.html',
  standalone: false,
})
export class DevsiteSearchComponent implements OnInit, OnDestroy {
  expanded = false;
  solutionFinderResponse: any;
  filterList: string[] = [];
  localSearchConfigs: any = [];
  localSearchProduct: string | null = null;
  searchControl = new FormControl('');
  selectedDocCategories: string[] = ['ALL_SELECTED'];
  searchResults$: Observable<SearchResponse | null> = of(null);
  showAllResults = false;
  readonly MAX_RESULTS = 5;
  pageSize: number;
  enableBackdrop = false;
  noResultMode = false;
  suggestedWord: string | null = null;
  lastQuery: string | null = null;
  isResponsiveScreen = false;
  resultSliceCount = 5;
  localSearchToggleActive = false;
  isLoading = false;
  placeholder = 'Ask AI';
  initialFacetResults: any = null;
  shouldUpdateFacetCacheOnNextResults = false;
  isSelectionEmpty = false;
  hasResults = false;

  private _resizeListener = () => this.handleResize();
  private routerEventsSub: Subscription;
  private documentClickListener: ((event: MouseEvent) => void) | null = null;
  private searchParamsSub: Subscription;
  private searchResultsSub: Subscription;
  private allResultsParamsSub: Subscription;
  private solutionFinderOnSub: Subscription;
  private lastUrl: string | null = null;

  @Output() expandedChange = new EventEmitter<boolean>();
  @Input() searchextended = false;
  @ViewChild(FilterComponent) filterComponent: FilterComponent;

  get selectedFilterValues(): string[] {
    if (this.selectedDocCategories.length === 1 && this.selectedDocCategories[0] === 'ALL_SELECTED') {
      return this.filterList;
    }
    return this.selectedDocCategories;
  }

  clearSearchInput() {
    this.searchControl.setValue('');
    this.onInputBlur(new FocusEvent('blur'));
  }

  toggleFilterDropdown(event: Event) {
    event.stopPropagation();
    if (this.filterComponent) {
      this.filterComponent.toggleDropdown(event);
    }
  }

  onInputBlur(event: FocusEvent) {
    if (this.isResponsiveScreen) return;
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget && relatedTarget.closest('.results-popup')) return;
    this.toggleBodyScroll(false);
    this.expanded = false;
    this.enableBackdrop = false;
    this.placeholder = 'Ask AI';
    this.expandedChange.emit(false);
  }

  constructor(
    private searchService: SearchService,
    private router: Router,
    private configService: ConfigService,
    private route: ActivatedRoute,
    private solutionFinderService: SolutionFinderService
  ) {}

  ngOnInit() {
    this.solutionFinderOnSub = this.solutionFinderService.closeInlineSearch.subscribe((close) => {
      if (close) {
       this.onInputBlur(new FocusEvent('blur'));
      }
    });
    const storedQuery = localStorage.getItem('allResultsSearchParams') || '';
    if (this.searchextended) {
      if (storedQuery) {
        localStorage.removeItem('allResultsSearchParams');
      }
      this.searchService.setParams({ query: storedQuery, pageNo: 1 }, SearchContext.Results);
    }

    this.handleResize();
    window.addEventListener('resize', this._resizeListener);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    this.routerEventsSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const currentUrl = event.urlAfterRedirects;
        if (!this.searchextended) {
          this.clearSearchInput();
        }
        if (this.lastUrl !== null && this.lastUrl !== currentUrl && this.isSelectionEmpty) {
          this.isSelectionEmpty = false;
          this.noResultMode = false;
        }
        this.lastUrl = currentUrl;

        this.resetLocalizationAndProduct();
        if (!currentUrl.includes('/results')) {
          this.searchService.setContext(SearchContext.Inline, false, true);
        } else {
          this.searchService.setContext(SearchContext.Results, false, false);
        }
        if (this.expanded) {
          this.expanded = false;
          this.placeholder = 'Ask AI';
          this.enableBackdrop = false;
          this.toggleBodyScroll(false);
          this.expandedChange.emit(false);
        }
      }
    });

    this.documentClickListener = (event: MouseEvent) => {
      if (this.isResponsiveScreen) return;
      const target = event.target as HTMLElement;
      const popup = document.querySelector('.results-popup');
      const input = document.getElementById('-search-input');
      if (this.expanded && popup && !popup.contains(target) && input && !input.contains(target)) {
        this.expanded = false;
        this.placeholder = 'Ask AI';
        this.enableBackdrop = false;
        this.toggleBodyScroll(false);
        this.expandedChange.emit(false);
      }
    };

    document.addEventListener('mousedown', this.documentClickListener);

    const paramsObservable = this.searchextended ? this.searchService.allResultsParams$ : this.searchService.params$;

    const paramsSub = paramsObservable.subscribe((params) => {
      if (params.query !== this.searchControl.value) {
        this.searchControl.setValue(params.query, { emitEvent: false });
      }
      if (params.categories === null) {
        this.selectedDocCategories = ['EMPTY'];
      } else if (params.categories && params.categories.length > 0) {
        this.selectedDocCategories = params.categories;
      } else {
        this.selectedDocCategories = ['ALL_SELECTED'];
      }
    });
    if (this.searchextended) {
      this.allResultsParamsSub = paramsSub;
    } else {
      this.searchParamsSub = paramsSub;
    }

    this.configService.getConfig('search-meta').subscribe((response) => {
      if (response.Items && response.Items.length > 0) {
        const searchMeta = response.Items[0];
        this.solutionFinderResponse = searchMeta['solutionfinder'][0];
        this.filterList = searchMeta['filterlabels'];
        const localPage = searchMeta['localizationpath'];
        if (localPage) {
          this.localizedSearchParam(localPage);
        }
      }
    });

    this.searchControl.valueChanges
      .pipe(
        startWith(storedQuery || ''),
        map((q) => q ?? ''),
        distinctUntilChanged(),
        debounceTime(400)
      )
      .subscribe((query) => {
        this.onSelectionChange(['ALL_SELECTED']);
        if (this.searchextended) {
          this.searchService.setParams({ query, pageNo: 1 }, SearchContext.Results);
        } else {
          this.searchService.setParams({ query, pageNo: 1 });
        }
      });

    this.searchResults$ = (
      this.searchextended ? this.searchService.allResultsParams$ : this.searchService.params$
    ).pipe(
      switchMap((params: SearchParams) => {
        if (!params.query || params.query.length < 3) return of(null);
        if (this.noResultMode) {
          return of({ ResultItems: [] } as SearchResponse);
        }
        this.isLoading = true;
        if (this.searchextended) {
          this.searchService.setAllResultsLoading(true);
        }
        const queryStr = this.searchService.getSearchQueryParam(
          params.query,
          params.pageNo,
          params.pageSize,
          params.categories,
          params.product
        );
        return this.searchService.getOmniSearch(queryStr).pipe(finalize(() => (this.isLoading = false)));
      }),
      map((resp: SearchResponse | null) => {
        if (!resp) {
          if (this.searchextended) {
            this.searchService.setAllResults({
              results: [],
              suggestedWord: null,
              currentQuery: this.searchControl.value || '',
              isLoading: false,
            });
          }
          this.hasResults = false;
          return null;
        }
        this.initLocalSearch();
        this.suggestedWord = resp.ResultItems.length ? null : resp.SpellCorrectedQueries;
        this.showAllResults = resp.ResultItems.length >= this.MAX_RESULTS;
        const currentQuery = this.searchControl.value || '';
        if (this.searchextended) {
          this.searchService.setAllResults({
            results: resp.ResultItems,
            suggestedWord: resp.SpellCorrectedQueries,
            currentQuery: currentQuery,
            isLoading: false,
          });
        }

        if (resp?.FacetResults && currentQuery.length >= 3) {
          if (this.lastQuery !== currentQuery || this.shouldUpdateFacetCacheOnNextResults) {
            this.initialFacetResults = resp.FacetResults;
            this.lastQuery = currentQuery;
            this.shouldUpdateFacetCacheOnNextResults = false;
            if (Object.keys(resp.FacetResults).length === 0 || this.suggestedWord) {
              this.initialFacetResults = false;
            }
          }
        }

        this.hasResults = ((!this.suggestedWord && resp.ResultItems?.length !== 0) || this.initialFacetResults);

        return resp;
      })
    );
    if (this.searchextended && this.searchResults$) {
      this.searchResultsSub = this.searchResults$.subscribe();
    }
  }

  ngOnDestroy() {
    if (this.documentClickListener) {
      document.removeEventListener('mousedown', this.documentClickListener);
    }
    this.routerEventsSub?.unsubscribe();
    this.searchParamsSub?.unsubscribe();
    this.searchResultsSub?.unsubscribe();
    this.allResultsParamsSub?.unsubscribe();
    window.removeEventListener('resize', this._resizeListener);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange() {
    if (document.hidden) {
      const input = document.getElementById('-search-input') as HTMLInputElement | null;
      if (input) input.blur();
    }
  }

  private toggleBodyScroll(disable: boolean) {
    if (disable && !(this.expanded && this.enableBackdrop)) {
      return;
    }
    if (disable) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('wheel', this.preventScroll, { passive: false });
      document.addEventListener('touchmove', this.preventScroll, { passive: false });
      document.addEventListener('keydown', this.preventScrollKeys, { passive: false });
    } else {
      document.body.style.overflow = '';
      document.removeEventListener('wheel', this.preventScroll);
      document.removeEventListener('touchmove', this.preventScroll);
      document.removeEventListener('keydown', this.preventScrollKeys);
    }
  }

  private preventScroll = (e: Event) => {
    if (this.isResponsiveScreen || !this.expanded || !this.enableBackdrop) {
      return;
    }
    const target = e.target as HTMLElement;
    if (target && target.closest('.results-popup')) {
      return;
    }
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
  };

  private preventScrollKeys = (e: KeyboardEvent) => {
    if (this.isResponsiveScreen) {
      return;
    }
    const target = e.target as HTMLElement;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true')) {
      return;
    }
    if (!this.searchextended && this.expanded && this.enableBackdrop) {
      const keys = ['PageUp', 'PageDown', 'End', 'Home', 'ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'];
      if (keys.includes(e.key)) e.preventDefault();
    }
  };

  private handleResize() {
    this.isResponsiveScreen = window.innerWidth < 1024;
    this.resultSliceCount = this.isResponsiveScreen ? 3 : 5;
  }

  applyCorrection(value: string) {
    this.searchControl.setValue(value);
  }

  onLocalSearchToggleChange(active: boolean) {
    this.localSearchToggleActive = active;
    this.onSelectionChange(['ALL_SELECTED']);
    this.localSearchProductHandler(active ? this.localSearchProduct : '');
    this.shouldUpdateFacetCacheOnNextResults = true;
  }

  onFocus() {
    if (!this.searchextended) {
      this.expanded = true;
      this.placeholder = 'Search by product, API, or keywords';
      this.expandedChange.emit(true);
      setTimeout(() => {
        this.enableBackdrop = true;
        this.toggleBodyScroll(true);
      }, 190);
    }
  }

  localizedSearchParam(localString: string) {
    this.route.url.subscribe(() => {
      if (this.localSearchConfigs && this.localSearchConfigs.length > 0) {
        this.initLocalSearch();
      } else {
        this.configService.getConfig(localString).subscribe((response) => {
          if (response.Items && response.Items.length > 0) {
            this.localSearchConfigs = response.Items[0]['url-to-product'];
            this.initLocalSearch();
          }
        });
      }
    });
  }

  private initLocalSearch() {
    const currentUrl = this.router.url.split('?')[0];
    this.localSearchProduct = null;
    this.localSearchConfigs.forEach((config: any) => {
      const parsed = this.parseRegex(config.regex);
      if (parsed) {
        try {
          const regex = new RegExp(parsed.pattern, parsed.flags);
          if (regex.test(currentUrl)) {
            this.localSearchProduct = config.product;
          }
        } catch {
          this.resetLocalizationAndProduct();
        }
      } else {
        this.resetLocalizationAndProduct();
      }
    });
  }

  private updateParams(
    changes: Partial<{ query: string; categories: string[]; product: string | null; pageNo: number; pageSize: number }>
  ) {
    this.searchService.setParams(changes);
  }

  localSearchProductHandler(product: string | null) {
    this.updateParams({ product: product || null, pageNo: 1 });
  }

  onSelectionChange(selected: string[]) {
    this.selectedDocCategories = selected;
    if (selected.length === 1 && selected[0] === 'EMPTY') {
      this.noResultMode = true;
      this.isSelectionEmpty = true;
      this.updateParams({ categories: null, pageNo: 1 });
      return;
    }

    this.noResultMode = false;
    this.isSelectionEmpty = false;

    if (selected.length === 1 && selected[0] === 'ALL_SELECTED') {
      this.updateParams({ categories: [], pageNo: 1 });
      return;
    }

    this.updateParams({ categories: selected, pageNo: 1 });
  }

  private parseRegex(regexString: string): { pattern: string; flags: string } | null {
    const match = regexString.match(/\/(.*)\/([gimsuy]*)/);
    return match ? { pattern: match[1], flags: match[2] } : null;
  }

  onShowAll(event: Event) {
    event.preventDefault();
    const element = document.getElementById('closeHamburger');
    if (element) element.click();
    const currentQuery = this.searchControl.value || '';
    if (this.router.url === '/results' || this.router.url.startsWith('/results?')) {
      this.searchService.setParams(
        {
          query: currentQuery,
          pageNo: 1,
        },
        SearchContext.Results
      );
      this.expanded = false;
      this.placeholder = 'Ask AI';
      this.enableBackdrop = false;
      this.toggleBodyScroll(false);
      this.expandedChange.emit(false);
      this.searchService.setParams({ query: '', pageNo: 1 });
    } else {
      localStorage.setItem('allResultsSearchParams', currentQuery);
      if (this.router.url.startsWith('/dashboard')) {
        window.location.href = '/results';
      } else {
        this.router.navigate(['/results']);
      }
    }
  }

  resetLocalizationAndProduct() {
    this.localSearchProduct = null;
    this.localSearchToggleActive = false;
    this.searchService.setParams({ product: null });
  }
}
